import React, { useMemo, useState } from 'react';
import PublicLayout from '../../components/layout/PublicLayout';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';

import AmazonOrderDemo from '../../amazon-order-ss.png';
import AmazonRatingVideo from '../../amazon-rating-video.mp4';
import AmazonRatingSS from '../../amazon-rating-ss.jpeg';
import AmazonReviewSubmitSS from '../../amazon-review-submit-ss.jpeg';
import AmazonDeliveredSS from '../../amazon-delivered-ss.jpeg';
import AmazonReviewSS from '../../amazon-review-ss.jpeg';

import MyntraOrderSS from '../../myntra-order-ss.jpeg';
import MyntraReviewSS from '../../myntra-review-ss.jpeg';

import NykaOrderSS from '../../nyka-order-ss.jpeg';
import NykaReviewSS from '../../nyka-review-ss.jpeg';

import FlipkartOrderDemo from '../../flipkart-order-ss.png';
import FlipkartDeliveredSS from '../../flipkart-delivered-ss.jpeg';

import MeeshoReviewSS from '../../meesho-reveiw-ss.jpeg';

import BlinkitOrderSS from '../../blinkit-order-ss.jpeg';
import BlinkitReviewSS from '../../blinkit-review-ss.jpeg';

import SwiggyOrderSS from '../../swiggy-order-ss.jpeg';
import SwiggyReviewSS from '../../swiggy-reveiw-ss.jpeg';

import JioMartOrderSS from '../../jiomart-order-ss.jpeg';
import JioMartRatingSS from '../../jiomart-rating-ss.jpeg';

const Guides = () => {
  const guides = useMemo(() => ([
    {
      key: 'amazon',
      title: 'Amazon',
      sections: [
        {
          title: 'Screenshot: Delivered Order Confirmation',
          steps: [
            'Go to the Amazon website or open the app and log in to your account.',
            'Navigate to "Your Orders" to see your full purchase history.',
            'Find the specific product you are reviewing. The order status must clearly show as "Delivered".',
            'Take a screenshot of this entire section, making sure these details are visible:',
            'The Product Name',
            'The word "Delivered" and the specific delivery date',
          ],
          note: 'A correct screenshot confirms that you have received the item you are reviewing.',
          media: [
            { type: 'image', label: 'Order SS (Demo)', src: AmazonOrderDemo },
            { type: 'image', label: 'Delivered SS', src: AmazonDeliveredSS },
          ]
        },
        {
          title: 'Screenshot / Video: Rating & Review',
          steps: [
            'Open your Amazon order and go to the review section.',
            'Submit rating/review as required by your deal.',
            'Capture the screenshot(s) so the rating/review is clearly visible.',
          ],
          note: 'Make sure your profile/account name and rating stars are visible where applicable.',
          media: [
            { type: 'video', label: 'Rating SS Video', src: AmazonRatingVideo },
            { type: 'image', label: 'Rating SS', src: AmazonRatingSS },
            { type: 'image', label: 'Review Submit SS', src: AmazonReviewSubmitSS },
            { type: 'image', label: 'Review SS', src: AmazonReviewSS },
          ]
        },
      ]
    },
    {
      key: 'myntra',
      title: 'Myntra',
      sections: [
        {
          title: 'Screenshot: Delivered Order Confirmation',
          steps: [
            'Open the Myntra website or app and log in to your account.',
            'Go to My Orders to view your purchase history.',
            'Locate the specific product you\'re reviewing with a "Delivered" status.',
            'Take a screenshot of the order details ensuring these are visible:',
            'Product Name',
            '"Delivered" status and the delivery date',
          ],
          note: 'A correct screenshot confirms you\'ve received the item you\'re reviewing.',
          media: [
            { type: 'image', label: 'Order SS', src: MyntraOrderSS },
            { type: 'image', label: 'Review SS', src: MyntraReviewSS },
          ]
        },
      ]
    },
    {
      key: 'nykaa',
      title: 'Nykaa',
      sections: [
        {
          title: 'Screenshot: Delivered Order Confirmation',
          steps: [
            'Open the Nykaa website or app and log in to your account.',
            'Go to My Orders to view your purchase history.',
            'Find the specific product you\'re reviewing with a "Delivered" status.',
            'Take a screenshot of the order details ensuring these are visible:',
            'Product Name',
            '"Delivered" status and the delivery date',
          ],
          note: 'A correct screenshot confirms you\'ve received the item you\'re reviewing.',
          media: [
            { type: 'image', label: 'Order SS', src: NykaOrderSS },
            { type: 'image', label: 'Review SS', src: NykaReviewSS },
          ]
        },
      ]
    },
    {
      key: 'flipkart',
      title: 'Flipkart',
      sections: [
        {
          title: 'Screenshot: Delivered Order Confirmation',
          steps: [
            'Open the Flipkart website or app and log in to your account.',
            'Go to Your Orders to view your purchase history.',
            'Locate the specific product you\'re reviewing with a status of "Delivered".',
            'Take a screenshot of the order details ensuring these are visible:',
            'Product Name',
            '"Delivered" status and the delivery date',
          ],
          note: 'A correct screenshot confirms you\'ve received the item you\'re reviewing.',
          media: [
            { type: 'image', label: 'Order SS (Demo)', src: FlipkartOrderDemo },
            { type: 'image', label: 'Delivered SS', src: FlipkartDeliveredSS },
          ]
        },
      ]
    },
    {
      key: 'meesho',
      title: 'Meesho',
      sections: [
        {
          title: 'Screenshot: Review',
          steps: [
            'Open Meesho and log in to your account.',
            'Go to orders and open your product.',
            'Submit review/rating if required.',
            'Take a screenshot showing the review clearly.',
          ],
          note: 'Make sure product name and your review are visible.',
          media: [
            { type: 'image', label: 'Review SS', src: MeeshoReviewSS },
          ]
        },
      ]
    },
    {
      key: 'blinkit',
      title: 'Blinkit',
      sections: [
        {
          title: 'Screenshots: Order & Review',
          steps: [
            'Open Blinkit and log in to your account.',
            'Open Orders and find your delivered product.',
            'Take an order screenshot showing delivered status.',
            'If review is required, submit and capture the review screenshot.',
          ],
          note: 'Ensure order/product name and delivered/review status is visible.',
          media: [
            { type: 'image', label: 'Order SS', src: BlinkitOrderSS },
            { type: 'image', label: 'Review SS', src: BlinkitReviewSS },
          ]
        },
      ]
    },
    {
      key: 'swiggy',
      title: 'Swiggy',
      sections: [
        {
          title: 'Screenshots: Order & Review',
          steps: [
            'Open Swiggy and log in to your account.',
            'Go to Orders and find your delivered order.',
            'Take a screenshot showing the order details.',
            'Submit review/rating if required and take screenshot.',
          ],
          note: 'Ensure order details and review are clearly visible.',
          media: [
            { type: 'image', label: 'Order SS', src: SwiggyOrderSS },
            { type: 'image', label: 'Review SS', src: SwiggyReviewSS },
          ]
        },
      ]
    },
    {
      key: 'jiomart',
      title: 'Jiomart',
      sections: [
        {
          title: 'Screenshots: Order & Rating',
          steps: [
            'Open Jiomart and log in to your account.',
            'Go to Orders and open the delivered order.',
            'Take a screenshot showing delivered order details.',
            'Submit rating if required and take screenshot showing rating.',
          ],
          note: 'Ensure product name and order/rating details are visible.',
          media: [
            { type: 'image', label: 'Order SS', src: JioMartOrderSS },
            { type: 'image', label: 'Rating SS', src: JioMartRatingSS },
          ]
        },
      ]
    },
  ]), []);

  const [openKey, setOpenKey] = useState('amazon');

  return (
    <PublicLayout title="Demo Screenshots & Steps" showBack onBackClick={() => window.history.back()}>
      <div style={{ maxWidth: '980px', margin: '0 auto' }}>
        <div style={{
          borderRadius: '16px',
          padding: '18px 18px',
          marginBottom: '18px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border)'
        }}>
          <div className="outfit" style={{ fontWeight: 800, fontSize: '22px', marginBottom: '6px' }}>
            How it works: Review & Rating Process
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
            Open a platform below to see the exact steps and demo screenshots/videos you need to upload.
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {guides.map((g) => {
            const isOpen = openKey === g.key;
            return (
              <div key={g.key} className="card" style={{ overflow: 'hidden' }}>
                <button
                  type="button"
                  onClick={() => setOpenKey(isOpen ? '' : g.key)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 18px',
                    background: 'var(--bg-card)',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <div className="outfit" style={{ fontWeight: 800, fontSize: '18px', color: 'var(--text)' }}>
                    {g.title}
                  </div>
                  <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={18} />
                </button>

                {isOpen && (
                  <div style={{ padding: '18px', borderTop: '1px solid var(--border)' }}>
                    {g.sections.map((s) => (
                      <div key={s.title} style={{ marginBottom: '22px' }}>
                        <div className="outfit" style={{ fontWeight: 800, fontSize: '14px', marginBottom: '8px' }}>
                          Process Steps
                        </div>
                        <div className="outfit" style={{ fontWeight: 800, fontSize: '14px', marginBottom: '10px' }}>
                          {s.title}
                        </div>

                        <ol style={{ margin: 0, paddingLeft: '18px', color: 'var(--text)' }}>
                          {s.steps.map((step, idx) => (
                            <li key={idx} style={{ marginBottom: '8px', color: 'var(--text)' }}>{step}</li>
                          ))}
                        </ol>

                        {s.note && (
                          <div style={{
                            marginTop: '10px',
                            padding: '12px',
                            borderRadius: '12px',
                            background: 'var(--bg-deep)',
                            border: '1px solid var(--border)',
                            color: 'var(--text)'
                          }}>
                            <span style={{ fontWeight: 800 }}>Important:</span> {s.note}
                          </div>
                        )}

                        {s.media?.length ? (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                            gap: '12px',
                            marginTop: '14px'
                          }}>
                            {s.media.map((m) => (
                              <div key={m.label} style={{
                                border: '1px solid var(--border)',
                                borderRadius: '14px',
                                overflow: 'hidden',
                                background: 'var(--bg-card)'
                              }}>
                                <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', fontSize: '13px', fontWeight: 800 }}>
                                  {m.label}
                                </div>
                                <div style={{ padding: '12px' }}>
                                  {m.type === 'video' ? (
                                    <video src={m.src} controls style={{ width: '100%', borderRadius: '10px' }} />
                                  ) : (
                                    <img src={m.src} alt={m.label} style={{ width: '100%', borderRadius: '10px' }} />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ))}

                    <Button
                      variant="secondary"
                      onClick={() => setOpenKey('')}
                      style={{ width: '100%', justifyContent: 'center' }}
                    >
                      Collapse
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </PublicLayout>
  );
};

export default Guides;
